import { translate } from "app/language/service"
import { getStore } from "app/store/store"
import { fillZero } from "app/tools/common"
import { formatDate } from "app/util/date/tools/common"
import { CURRENCIES } from "currency/model"
import { getValueInUserCurrency } from "currency/tools/common"
import { getWeek } from "date-fns"
import { getCurrencySymbol } from "form/inventory/tools/common"
import {
  IGetReportDataResult,
  IGetReportResult,
  LESS_THAN_15_DAYS_FILTER_KEY,
  LESS_THAN_3_MONTHS_FILTER_KEY,
  LESS_THAN_ONE_DAY_FILTER_KEY,
  LESS_THAN_ONE_MONTH_FILTER_KEY,
  LESS_THAN_ONE_YEAR_FILTER_KEY,
  LESS_THAN_SIX_MONTH_FILTER_KEY,
  REVENU_GROUP_BYS
} from "reports/model"
import { reportsActionCreator } from "reports/store/actions"
import { IChartDataSet } from "ui/configuration/model"
import { ITimeFilter } from "ui/model"
import { getUserCurrencyId } from "user/tools/common"

export const refreshGroupedByRevenueReports = async (timeFilter: ITimeFilter): Promise<void> => {
  getStore().dispatch(reportsActionCreator.startLoadingRevenu())
  try {
    const groupBy = getGroupByFromTimeFilter(timeFilter)
    const result = getFakeData(groupBy)
    /* const result = await reportsRPO.getResults({
      groupBys: [groupBy],
      filters: { startDate: timeFilter.date?.toISOString() },
      sortBys: [groupBy]
    })
    */
    if (result.data.length === 0) {
      getStore().dispatch(reportsActionCreator.setRevenu([], null))
    } else {
      getStore().dispatch(
        reportsActionCreator.setRevenu(
          getChartDataSetFromRPOResult(result, groupBy, timeFilter.date as Date),
          result.totals
        )
      )
    }
  } catch (error) {
    console.error("Unable to refresh revenues", error)
    getStore().dispatch(reportsActionCreator.setRevenuError())
  }
}

const getChartDataSetFromRPOResult = (
  result: IGetReportResult,
  groupBy: REVENU_GROUP_BYS,
  startDate: Date
): IChartDataSet[] => {
  const xLabels = getXLabels(groupBy, startDate)
  const bidCharset: any = {
    backgroundColor: "rgba(175, 31, 52, 0.2)",
    borderColor: "#af1f34",
    borderWidth: 2,
    borderRadius: 5,
    borderSkipped: false,
    label: getRevenueChartLabelBids(),
    data: xLabels.map(xLabel => ({ x: xLabel, y: 0 }))
  }
  const buyCharset: IChartDataSet = {
    backgroundColor: "rgba(0, 119, 182, .2)",
    borderColor: "#0077b6",
    borderWidth: 2,
    borderRadius: 5,
    borderSkipped: false,
    label: getRevenueChartLabelBuys(),
    data: xLabels.map(xLabel => ({ x: xLabel, y: 0 }))
  }
  const winsCharset: IChartDataSet = {
    backgroundColor: "rgba(212, 163, 115, .2)",
    borderColor: "#d4a373",
    borderWidth: 2,
    borderRadius: 5,
    borderSkipped: false,
    label: getRevenueChartLabelWin(),
    data: xLabels.map(xLabel => ({ x: xLabel, y: 0 }))
  }
  const impressionsCharset: IChartDataSet = {
    backgroundColor: "rgba(255, 213, 0, .2)",
    borderColor: "#ffd500",
    borderWidth: 2,
    borderRadius: 5,
    borderSkipped: false,
    label: getRevenueChartLabelImpression(),
    data: xLabels.map(xLabel => ({ x: xLabel, y: 0 }))
  }
  const currencyGrowSpendCharset: IChartDataSet = {
    backgroundColor: "rgba(212, 163, 115, .2)",
    borderColor: "#d4a373",
    borderWidth: 2,
    borderRadius: 5,
    borderSkipped: false,
    label: getRevenueChartLabelGrow(),
    data: xLabels.map(xLabel => ({ x: xLabel, y: 0 }))
  }
  const chartDataSets: IChartDataSet[] = [
    bidCharset,
    winsCharset,
    buyCharset,
    impressionsCharset,
    currencyGrowSpendCharset
  ]
  for (const data of result.data) {
    const xLabel = getXLabelFromGroupBy(data, groupBy)
    const indexOf = xLabels.indexOf(xLabel)
    if (indexOf === -1) {
      bidCharset.data.push({
        x: xLabel,
        y: data.bids
      })
      buyCharset.data.push({
        x: xLabel,
        y: data.buys
      })
      winsCharset.data.push({
        x: xLabel,
        y: data.wins
      })
      impressionsCharset.data.push({
        x: xLabel,
        y: data.impressions
      })
      currencyGrowSpendCharset.data.push({
        x: xLabel,
        y: getValueInUserCurrency(CURRENCIES.CAD, data.spend || 0)
      })
    } else {
      bidCharset.data[indexOf].y = data.bids
      buyCharset.data[indexOf].y = data.buys
      winsCharset.data[indexOf].y = data.wins
      impressionsCharset.data[indexOf].y = data.impressions
      currencyGrowSpendCharset.data[indexOf].y = getValueInUserCurrency(CURRENCIES.CAD, data.spend || 0)
    }
  }
  return chartDataSets
}

export const getRevenueChartLabelGrow = () =>
  `${getCurrencySymbol(getUserCurrencyId())} ${translate("reports.column.currency_spend")}`

export const getRevenueChartLabelImpression = () => translate("reports.column.impressions")

export const getRevenueChartLabelWin = () => translate("reports.column.wins")

export const getRevenueChartLabelBuys = () => translate("reports.column.buys")

export const getRevenueChartLabelBids = () => translate("reports.column.bids")

const getXLabels = (groupBy: REVENU_GROUP_BYS, startDate: Date): string[] => {
  switch (groupBy) {
    case REVENU_GROUP_BYS.HOUR: {
      return getXLabelsHour(startDate)
    }
    case REVENU_GROUP_BYS.DAY: {
      return getXLabelsDay(startDate)
    }
    case REVENU_GROUP_BYS.WEEK: {
      return getXLabelsWeek(startDate)
    }
    case REVENU_GROUP_BYS.MONTH: {
      return getXLabelsMonth(startDate)
    }
    default: {
      throw Error(`Unsupported groupBy ${groupBy}`)
    }
  }
}

const getXLabelsHour = (startDate: Date): string[] => {
  startDate = resetDate(startDate)
  const now = resetDate(new Date())
  const result: string[] = []
  while (startDate < now) {
    result.push(formatDate(startDate, { displayTime: true }))
    startDate.setUTCHours(startDate.getUTCHours() + 1)
  }
  return result
}

const getXLabelsDay = (startDate: Date): string[] => {
  startDate = resetDate(startDate)
  const now = resetDate(new Date())
  const result: string[] = []
  while (startDate < now) {
    result.push(formatDate(startDate))
    startDate.setDate(startDate.getDate() + 1)
  }
  return result
}

const getXLabelsWeek = (startDate: Date): string[] => {
  startDate = resetDate(startDate)
  const now = resetDate(new Date())
  const result: string[] = []
  while (startDate < now) {
    const weekNumber = getWeek(startDate)
    result.push(`${startDate.getFullYear()}-W${fillZero(weekNumber)}`)
    startDate.setDate(startDate.getDate() + 7)
  }
  return result
}

const getXLabelsMonth = (startDate: Date): string[] => {
  startDate = resetDate(startDate)
  const now = resetDate(new Date())
  const result: string[] = []
  while (startDate < now) {
    result.push(startDate.toISOString().substring(0, 7))
    startDate.setMonth(startDate.getMonth() + 1)
  }
  return result
}

const resetDate = (date: Date): Date => {
  const dateCopy = new Date(date)
  dateCopy.setMinutes(0)
  dateCopy.setSeconds(0)
  dateCopy.setMilliseconds(0)
  return dateCopy
}

const getGroupByFromTimeFilter = (timeFilter: ITimeFilter): REVENU_GROUP_BYS => {
  switch (timeFilter.labelKey) {
    case LESS_THAN_ONE_DAY_FILTER_KEY: {
      return REVENU_GROUP_BYS.HOUR
    }
    case LESS_THAN_15_DAYS_FILTER_KEY:
    case LESS_THAN_ONE_MONTH_FILTER_KEY: {
      return REVENU_GROUP_BYS.DAY
    }
    case LESS_THAN_3_MONTHS_FILTER_KEY:
    case LESS_THAN_SIX_MONTH_FILTER_KEY: {
      return REVENU_GROUP_BYS.WEEK
    }
    case LESS_THAN_ONE_YEAR_FILTER_KEY: {
      return REVENU_GROUP_BYS.MONTH
    }
    default: {
      throw Error(`Unsupported time filter ${timeFilter.labelKey}`)
    }
  }
}

const getXLabelFromGroupBy = (data: IGetReportDataResult, groupBy: REVENU_GROUP_BYS): string => {
  switch (groupBy) {
    case REVENU_GROUP_BYS.HOUR: {
      return formatDate(data.hour || "-", { displayTime: true })
    }
    case REVENU_GROUP_BYS.DAY: {
      return formatDate(data.day || "-")
    }
    case REVENU_GROUP_BYS.WEEK: {
      // "2025-W08"
      return data.week || "-"
    }
    case REVENU_GROUP_BYS.MONTH: {
      // "2025-02-01"
      let month = data.month || "-"
      if (month.length > 6) {
        month = month.substring(0, 7)
      }
      return month
    }
    default: {
      throw Error(`Unsupported groupBy ${groupBy}`)
    }
  }
}

const getFakeData = (groupBy: REVENU_GROUP_BYS): IGetReportResult => {
  switch (groupBy) {
    case REVENU_GROUP_BYS.HOUR:
      return getFakeHourData()
    case REVENU_GROUP_BYS.DAY:
      return getFakeDayData()
    case REVENU_GROUP_BYS.WEEK:
      return getFakeWeekData()
    case REVENU_GROUP_BYS.MONTH:
      return getFakeMonthData()
  }
}

const getFakeHourData = (): IGetReportResult => ({
  totals: {
    bids: 4532,
    wins: 3532,
    impressions: 6000,
    buys: 3488,
    spend: 4999
  } as any,
  data: [
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 4202,
      wins: 4124,
      buys: 4200,
      impressions: 4200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 41.24002378121285,
      hour: "2025-02-22T00:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 1202,
      wins: 2124,
      buys: 3200,
      impressions: 5200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 11.24002378121285,
      hour: "2025-02-22T01:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 2202,
      wins: 3124,
      buys: 1200,
      impressions: 6200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 21.24002378121285,
      hour: "2025-02-22T02:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 5202,
      wins: 5124,
      buys: 4200,
      impressions: 1200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 51.24002378121285,
      hour: "2025-02-22T03:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 4202,
      wins: 4124,
      buys: 3200,
      impressions: 1200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 41.24002378121285,
      hour: "2025-02-22T04:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 6202,
      wins: 2124,
      buys: 3200,
      impressions: 6200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 61.24002378121285,
      hour: "2025-02-22T05:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 4202,
      wins: 4124,
      buys: 2200,
      impressions: 8200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 31.24002378121285,
      hour: "2025-02-22T06:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 4202,
      wins: 4124,
      buys: 2200,
      impressions: 3200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 41.24002378121285,
      hour: "2025-02-22T07:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 4202,
      wins: 1124,
      buys: 3200,
      impressions: 6200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 11.24002378121285,
      hour: "2025-02-22T08:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 4202,
      wins: 2124,
      buys: 4200,
      impressions: 6200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 31.24002378121285,
      hour: "2025-02-22T09:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 1202,
      wins: 3124,
      buys: 1200,
      impressions: 9200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 21.24002378121285,
      hour: "2025-02-22T10:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 4202,
      wins: 3124,
      buys: 4200,
      impressions: 5200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 41.24002378121285,
      hour: "2025-02-22T11:00:00" // "hour": "2025-02-22T12:00:00"
    } as any,
    {
      ssp_id: 37,
      campaign_id: 48815,
      bids: 4202,
      wins: 2124,
      buys: 7200,
      impressions: 11200,
      cad_gross_spend: 59.01993700000001,
      currency_id: "USD",
      spend: 71.24002378121285,
      hour: "2025-02-22T12:00:00" // "hour": "2025-02-22T12:00:00"
    } as any
  ]
})

const getFakeDayData = (): IGetReportResult => ({
  totals: {
    bids: 26327,
    wins: 23373,
    impressions: 23373,
    buys: 23373,
    spend: 388.5642450000002,
    ssp_spend: 333.14620999999966,
    delivery_fees: 55.41803499999997,
    screens: 3,
    medias: 1
  },
  data: [
    {
      bids: 2804,
      wins: 2762,
      impressions: 2762,
      buys: 2762,
      spend: 46.13920033333338,
      ssp_spend: 39.54788599999997,
      delivery_fees: 6.591314333333335,
      ecpm: 16.7049965001207,
      day: "2025-02-24",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 4218,
      wins: 4144,
      impressions: 4144,
      buys: 4144,
      spend: 69.17559249999998,
      ssp_spend: 59.29336499999995,
      delivery_fees: 9.8822275,
      ecpm: 16.692951858108103,
      day: "2025-02-25",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 4240,
      wins: 4175,
      impressions: 4175,
      buys: 4175,
      spend: 69.63830416666671,
      ssp_spend: 59.68997499999991,
      delivery_fees: 9.948329166666651,
      ecpm: 16.679833333333345,
      day: "2025-02-26",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 4072,
      wins: 3991,
      impressions: 3991,
      buys: 3991,
      spend: 66.3628548333334,
      ssp_spend: 56.882446999999935,
      delivery_fees: 9.480407833333324,
      ecpm: 16.62812699407001,
      day: "2025-02-27",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 4152,
      wins: 4088,
      impressions: 4088,
      buys: 4088,
      spend: 67.5975358333334,
      ssp_spend: 57.94074499999995,
      delivery_fees: 9.656790833333327,
      ecpm: 16.535600742009144,
      day: "2025-02-28",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 1064,
      wins: 1046,
      impressions: 1046,
      buys: 1046,
      spend: 17.31653000000001,
      ssp_spend: 14.842739999999978,
      delivery_fees: 2.4737899999999975,
      ecpm: 16.55500000000001,
      day: "2025-03-01",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 2534,
      wins: 2500,
      impressions: 2500,
      buys: 2500,
      spend: 41.38391600000003,
      ssp_spend: 35.471927999999956,
      delivery_fees: 5.911988,
      ecpm: 16.553566400000012,
      day: "2025-03-02",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 124,
      wins: 101,
      impressions: 101,
      buys: 101,
      spend: 1.6718193333333324,
      ssp_spend: 1.4329880000000006,
      delivery_fees: 0.23883133333333334,
      ecpm: 16.552666666666656,
      day: "2025-03-03",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 538,
      wins: 522,
      impressions: 522,
      buys: 522,
      spend: 8.640491999999998,
      ssp_spend: 7.406136000000007,
      delivery_fees: 1.234355999999999,
      ecpm: 16.552666666666664,
      day: "2025-03-04",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 2581,
      wins: 44,
      impressions: 44,
      buys: 44,
      spend: 0.6380000000000005,
      ssp_spend: 0.6380000000000005,
      delivery_fees: 0,
      ecpm: 14.50000000000001,
      day: "2025-03-05",
      media_count: 1,
      screen_count: 1
    }
  ]
})

const getFakeWeekData = (): IGetReportResult => ({
  totals: {
    bids: 156006,
    wins: 49606,
    impressions: 65837,
    buys: 65835,
    spend: 128943.5859874666,
    ssp_spend: 110523.16484640002,
    delivery_fees: 18420.421141066676,
    screens: 11,
    medias: 10
  },
  data: [
    {
      bids: 925,
      wins: 0,
      impressions: 306,
      buys: 306,
      spend: 5.0652548333333325,
      ssp_spend: 4.341647,
      delivery_fees: 0.7236078333333331,
      ecpm: 16.55312037037037,
      week: "2024-W50",
      media_count: 3,
      screen_count: 1
    },
    {
      bids: 886,
      wins: 0,
      impressions: 274,
      buys: 274,
      spend: 4.5786568333333335,
      ssp_spend: 3.924563,
      delivery_fees: 0.6540938333333333,
      ecpm: 16.710426399026762,
      week: "2024-W51",
      media_count: 3,
      screen_count: 1
    },
    {
      bids: 5131,
      wins: 0,
      impressions: 2718,
      buys: 2718,
      spend: 45.62964233333332,
      ssp_spend: 39.11112199999998,
      delivery_fees: 6.518520333333331,
      ecpm: 16.787947878341914,
      week: "2024-W52",
      media_count: 3,
      screen_count: 1
    },
    {
      bids: 1593,
      wins: 0,
      impressions: 793,
      buys: 793,
      spend: 13.364921499999998,
      ssp_spend: 11.455646999999997,
      delivery_fees: 1.9092745000000013,
      ecpm: 16.853621059268598,
      week: "2025-W01",
      media_count: 3,
      screen_count: 1
    },
    {
      bids: 87358,
      wins: 0,
      impressions: 11931,
      buys: 11931,
      spend: 63.83827496666663,
      ssp_spend: 54.7185214,
      delivery_fees: 9.119753566666667,
      ecpm: 5.3506223255943866,
      week: "2025-W02",
      media_count: 7,
      screen_count: 6
    },
    {
      bids: 3042,
      wins: 784,
      impressions: 993,
      buys: 991,
      spend: 127997.1339091666,
      ssp_spend: 109711.829065,
      delivery_fees: 18285.304844166672,
      ecpm: 128899.42991859678,
      week: "2025-W03",
      media_count: 3,
      screen_count: 3
    },
    {
      bids: 10863,
      wins: 5825,
      impressions: 5825,
      buys: 5825,
      spend: 97.77631850000007,
      ssp_spend: 83.80827299999999,
      delivery_fees: 13.968045499999981,
      ecpm: 16.78563407725323,
      week: "2025-W05",
      media_count: 3,
      screen_count: 4
    },
    {
      bids: 15349,
      wins: 15185,
      impressions: 15185,
      buys: 15185,
      spend: 253.51172700000006,
      ssp_spend: 217.29576599999993,
      delivery_fees: 36.21596100000001,
      ecpm: 16.694878300954894,
      week: "2025-W06",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 25082,
      wins: 24645,
      impressions: 24645,
      buys: 24645,
      spend: 410.3530550000003,
      ssp_spend: 351.73118999999963,
      delivery_fees: 58.62186499999997,
      ecpm: 16.650560154189503,
      week: "2025-W07",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 3196,
      wins: 3123,
      impressions: 3123,
      buys: 3123,
      spend: 51.69622733333336,
      ssp_spend: 44.31105199999996,
      delivery_fees: 7.385175333333332,
      ecpm: 16.553386914291824,
      week: "2025-W08",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 2581,
      wins: 44,
      impressions: 44,
      buys: 44,
      spend: 0.6380000000000005,
      ssp_spend: 0.6380000000000005,
      delivery_fees: 0,
      ecpm: 14.50000000000001,
      week: "2025-W10",
      media_count: 1,
      screen_count: 1
    }
  ]
})

const getFakeMonthData = (): IGetReportResult => ({
  totals: {
    bids: 1434357,
    wins: 50602,
    impressions: 10287025,
    buys: 69539,
    spend: 266937.2704736506,
    ssp_spend: 225518.953749084,
    delivery_fees: 41418.31672456667,
    screens: 18,
    medias: 12
  },
  data: [
    {
      bids: 996,
      wins: 996,
      impressions: 10218,
      buys: 996,
      spend: 137949.475401684,
      ssp_spend: 114957.89540168401,
      delivery_fees: 22991.58,
      ecpm: 13.49999955,
      month: "2024-04-01",
      media_count: 2,
      screen_count: 6
    },
    {
      bids: 2,
      wins: 0,
      impressions: 0,
      buys: 0,
      spend: 0,
      ssp_spend: 0,
      delivery_fees: 0,
      ecpm: 0,
      month: "2024-06-01",
      media_count: 1,
      screen_count: 1
    },
    {
      bids: 468264,
      wins: 0,
      impressions: 0,
      buys: 0,
      spend: 0,
      ssp_spend: 0,
      delivery_fees: 0,
      ecpm: 0,
      month: "2024-10-01",
      media_count: 2,
      screen_count: 1
    },
    {
      bids: 809089,
      wins: 0,
      impressions: 2708,
      buys: 2708,
      spend: 44.20908450000001,
      ssp_spend: 37.89350099999999,
      delivery_fees: 6.3155835,
      ecpm: 16.325363552437228,
      month: "2024-11-01",
      media_count: 3,
      screen_count: 1
    },
    {
      bids: 6942,
      wins: 0,
      impressions: 3298,
      buys: 3298,
      spend: 55.273554,
      ssp_spend: 47.37733199999998,
      delivery_fees: 7.896221999999998,
      ecpm: 16.759719223771985,
      month: "2024-12-01",
      media_count: 3,
      screen_count: 1
    },
    {
      bids: 102856,
      wins: 6609,
      impressions: 19542,
      buys: 19540,
      spend: 128172.11342413326,
      ssp_spend: 109861.8115064,
      delivery_fees: 18310.301917733337,
      ecpm: 6558.802242561317,
      month: "2025-01-01",
      media_count: 10,
      screen_count: 11
    },
    {
      bids: 43627,
      wins: 42953,
      impressions: 42953,
      buys: 42953,
      spend: 715.5610093333338,
      ssp_spend: 613.3380079999995,
      delivery_fees: 102.2230013333333,
      ecpm: 16.659162557524127,
      month: "2025-02-01",
      media_count: 1,
      screen_count: 2
    },
    {
      bids: 2581,
      wins: 44,
      impressions: 44,
      buys: 44,
      spend: 0.6380000000000005,
      ssp_spend: 0.6380000000000005,
      delivery_fees: 0,
      ecpm: 14.50000000000001,
      month: "2025-03-01",
      media_count: 1,
      screen_count: 1
    }
  ]
})
